/**
 * 累积拼接工具函数
 * 实现新图片与当前结果的二元拼接
 */

export interface ImageInfo {
  url: string;
  width: number;
  height: number;
}

export interface CompositionResult {
  url: string;
  width: number;
  height: number;
  arrangement: 'horizontal' | 'vertical';
  aspectRatio: number;
}

/**
 * 计算两张图片拼接的最优排列方式
 * @param existingImage 现有图片信息
 * @param newImage 新图片信息
 * @returns 拼接结果信息
 */
export function calculateOptimalComposition(existingImage: ImageInfo, newImage: ImageInfo): CompositionResult {
  // 计算水平排列（左右拼接）
  const horizontal = calculateHorizontalArrangement(existingImage, newImage);

  // 计算垂直排列（上下拼接）
  const vertical = calculateVerticalArrangement(existingImage, newImage);

  // 选择长宽比更接近正方形的排列方式（比值更小的）
  return horizontal.aspectRatio <= vertical.aspectRatio ? horizontal : vertical;
}

/**
 * 计算水平排列（左右拼接）
 * 高度对齐，新图片宽度按比例缩放
 */
function calculateHorizontalArrangement(existingImage: ImageInfo, newImage: ImageInfo): CompositionResult {
  const targetHeight = existingImage.height;
  const newImageScaledWidth = Math.round((newImage.width * targetHeight) / newImage.height);

  const width = existingImage.width + newImageScaledWidth;
  const height = targetHeight;
  const aspectRatio = Math.max(width, height) / Math.min(width, height);

  return {
    url: '', // 将在Canvas渲染后设置
    width,
    height,
    arrangement: 'horizontal',
    aspectRatio,
  };
}

/**
 * 计算垂直排列（上下拼接）
 * 宽度对齐，新图片高度按比例缩放
 */
function calculateVerticalArrangement(existingImage: ImageInfo, newImage: ImageInfo): CompositionResult {
  const targetWidth = existingImage.width;
  const newImageScaledHeight = Math.round((newImage.height * targetWidth) / newImage.width);

  const width = targetWidth;
  const height = existingImage.height + newImageScaledHeight;
  const aspectRatio = Math.max(width, height) / Math.min(width, height);

  return {
    url: '', // 将在Canvas渲染后设置
    width,
    height,
    arrangement: 'vertical',
    aspectRatio,
  };
}

/**
 * 在Canvas上渲染拼接后的图片
 * @param existingImage 现有图片信息
 * @param newImage 新图片信息
 * @param composition 拼接结果信息
 * @param usePixelControl 是否使用像素数控制
 * @returns Promise<string> 返回拼接后的图片DataURL
 */
export async function renderCompositionToCanvas(
  existingImage: ImageInfo,
  newImage: ImageInfo,
  composition: CompositionResult,
  usePixelControl: boolean = false,
): Promise<string> {
  console.log('开始Canvas渲染:', { existingImage, newImage, composition, usePixelControl });

  // 如果启用像素数控制，计算目标尺寸
  let targetWidth = composition.width;
  let targetHeight = composition.height;

  if (usePixelControl) {
    const { calculateTargetDimensions } = await import('./pixel-control-utils');
    const targetDimensions = calculateTargetDimensions(composition.aspectRatio);
    targetWidth = targetDimensions.width;
    targetHeight = targetDimensions.height;
    console.log('像素数控制目标尺寸:', targetWidth, 'x', targetHeight);
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('无法获取Canvas上下文');
  }

  // 设置Canvas尺寸
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  console.log('Canvas尺寸设置:', canvas.width, 'x', canvas.height);

  // 设置高质量渲染
  ctx.imageSmoothingEnabled = true;
  if (typeof ctx.imageSmoothingQuality === 'string') {
    ctx.imageSmoothingQuality = 'high';
  }

  // 清空Canvas
  ctx.clearRect(0, 0, targetWidth, targetHeight);

  // 加载并绘制图片
  console.log('开始加载图片...');
  const [existingImg, newImg] = await Promise.all([loadImage(existingImage.url), loadImage(newImage.url)]);
  console.log('图片加载完成:', existingImg.width, 'x', existingImg.height, '和', newImg.width, 'x', newImg.height);

  // 绘制图片 - 根据是否使用像素数控制调整绘制逻辑
  if (usePixelControl) {
    // 像素数控制模式：按目标尺寸缩放整个拼接结果
    const scaleX = targetWidth / composition.width;
    const scaleY = targetHeight / composition.height;
    console.log('像素数控制缩放比例:', { scaleX, scaleY });

    if (composition.arrangement === 'horizontal') {
      // 水平排列：左右拼接
      const originalTargetHeight = composition.height;
      const newImageScaledWidth = Math.round((newImage.width * originalTargetHeight) / newImage.height);

      // 绘制现有图片（左侧）
      ctx.drawImage(existingImg, 0, 0, existingImage.width * scaleX, originalTargetHeight * scaleY);

      // 绘制新图片（右侧）
      ctx.drawImage(
        newImg,
        existingImage.width * scaleX,
        0,
        newImageScaledWidth * scaleX,
        originalTargetHeight * scaleY,
      );
    } else {
      // 垂直排列：上下拼接
      const originalTargetWidth = composition.width;
      const newImageScaledHeight = Math.round((newImage.height * originalTargetWidth) / newImage.width);

      // 绘制现有图片（上方）
      ctx.drawImage(existingImg, 0, 0, originalTargetWidth * scaleX, existingImage.height * scaleY);

      // 绘制新图片（下方）
      ctx.drawImage(
        newImg,
        0,
        existingImage.height * scaleY,
        originalTargetWidth * scaleX,
        newImageScaledHeight * scaleY,
      );
    }
  } else {
    // 原有逻辑：按原始拼接尺寸绘制
    if (composition.arrangement === 'horizontal') {
      // 水平排列：左右拼接
      const targetHeight = existingImage.height;
      const newImageScaledWidth = Math.round((newImage.width * targetHeight) / newImage.height);
      console.log('水平拼接参数:', { targetHeight, newImageScaledWidth });

      // 绘制现有图片（左侧）
      ctx.drawImage(existingImg, 0, 0, existingImage.width, existingImage.height);
      console.log('绘制现有图片:', 0, 0, existingImage.width, existingImage.height);

      // 绘制新图片（右侧）
      ctx.drawImage(newImg, existingImage.width, 0, newImageScaledWidth, targetHeight);
      console.log('绘制新图片:', existingImage.width, 0, newImageScaledWidth, targetHeight);
    } else {
      // 垂直排列：上下拼接
      const targetWidth = existingImage.width;
      const newImageScaledHeight = Math.round((newImage.height * targetWidth) / newImage.width);
      console.log('垂直拼接参数:', { targetWidth, newImageScaledHeight });

      // 绘制现有图片（上方）
      ctx.drawImage(existingImg, 0, 0, existingImage.width, existingImage.height);
      console.log('绘制现有图片:', 0, 0, existingImage.width, existingImage.height);

      // 绘制新图片（下方）
      ctx.drawImage(newImg, 0, existingImage.height, targetWidth, newImageScaledHeight);
      console.log('绘制新图片:', 0, existingImage.height, targetWidth, newImageScaledHeight);
    }
  }

  // 返回拼接后的图片DataURL - 🎯 改为JPEG格式
  const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
  console.log('Canvas渲染完成，DataURL长度:', dataUrl.length);
  return dataUrl;
}

/**
 * 加载图片元素
 * @param url 图片URL
 * @returns Promise<HTMLImageElement>
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    const timeout = setTimeout(() => {
      reject(new Error('图片加载超时'));
    }, 10000);

    img.onload = () => {
      clearTimeout(timeout);
      resolve(img);
    };

    img.onerror = (error) => {
      clearTimeout(timeout);
      reject(new Error(`图片加载失败: ${error}`));
    };

    // 处理跨域问题
    if (url.startsWith('http')) {
      img.crossOrigin = 'anonymous';
    }

    img.src = url;
  });
}
